import React from "react";
import type { Components } from "react-markdown";
import { ChartBlock } from "./components/ChartBlock";
import { ExternalLink } from "lucide-react";

// Custom components for enhanced rendering
export const components: Components = {
  code({ node, className, children, ...props }) {
    const match = /language-(\w+)/.exec(className || "");
    const language = match ? match[1] : "";

    console.debug(node);

    if (language === "chart") {
      return <ChartBlock code={String(children).replace(/\n$/, "")} />;
    }

    return (
      <code className={className} {...props}>
        {children}
      </code>
    );
  },
  // Headers with proper styling and anchor links
  h1: ({ children, ref, node, ...props }) => (
    <h1 className="text-4xl font-medium mt-12 mb-4 border-b pb-2" {...props}>
      {children}
    </h1>
  ),
  h2: ({ children, ref, node, ...props }) => (
    <h2 className="text-2xl font-medium mt-10 mb-3 border-b pb-1" {...props}>
      {children}
    </h2>
  ),
  h3: ({ children, ref, node, ...props }) => (
    <h3 className="text-xl font-medium mt-10 mb-3" {...props}>
      {children}
    </h3>
  ),
  h4: ({ children, ref, node, ...props }) => (
    <h4 className="text-lg font-medium mt-1 mb-2 text-foreground/90" {...props}>
      {children}
    </h4>
  ),
  h5: ({ children, ref, node, ...props }) => (
    <h5 className="text-base font-light mt-4 mb-1" {...props}>
      {children}
    </h5>
  ),
  h6: ({ children, ref, node, ...props }) => (
    <h6 className="text-sm font-light mt-3 mb-1" {...props}>
      {children}
    </h6>
  ),

  // Paragraphs - generous spacing
  p: ({ children, ref, node, ...props }) => (
    <p className="mb-6 leading-relaxed" {...props}>
      {children}
    </p>
  ),

  // Lists - deliberate spacing
  ul: ({ children, ref, node, ...props }) => (
    <ul className="list-disc pl-5 mb-6 space-y-3" {...props}>
      {children}
    </ul>
  ),
  ol: ({ children, ref, node, ...props }) => (
    <ol className="list-decimal pl-5 mb-6 space-y-3" {...props}>
      {children}
    </ol>
  ),
  li: ({ children, ref, node, ...props }) => (
    <li className="leading-relaxed" {...props}>
      {children}
    </li>
  ),

  // Tables - institutional style: minimal chrome, data-forward
  table: ({ children, ref, node, ...props }) => (
    <div className="overflow-x-auto rounded border border-primary/30 bg-primary/10">
      <table ref={ref} className="min-w-full mb-0 border-collapse" {...props}>
        {children}
      </table>
    </div>
  ),

  thead: ({ children, ...props }) => (
    <thead className="bg-primary/20 border-b border-primary/30" {...props}>
      {children}
    </thead>
  ),

  tbody: ({ children, ...props }) => (
    <tbody className="bg-background/50" {...props}>
      {children}
    </tbody>
  ),

  tr: ({ children, ...props }) => (
    <tr
      className="border-b border-primary/20 last:border-0 hover:bg-primary/10 transition-colors"
      {...props}
    >
      {children}
    </tr>
  ),

  th: ({ children, ...props }) => (
    <th
      className="px-4 py-3 text-left min-w-40 font-medium text-foreground/60 first:pl-3! last:pr-3!"
      {...props}
    >
      {children}
    </th>
  ),

  td: ({ children, ...props }) => (
    <td className="px-4 py-3 first:pl-3! last:pr-3!" {...props}>
      {children}
    </td>
  ),
  // Links with external icon
  a: ({ children, ref, node, href, ...props }) => (
    <a
      href={href}
      className="inline-flex items-center gap-1 text-blue-400 hover:text-blue-300 hover:underline"
      target="_blank"
      rel="noopener noreferrer"
      {...props}
    >
      {children}
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  ),

  // Images with error handling
  img: ({ src, alt, title, children, ref, ...props }) => {
    if (!src) {
      return (
        <div className="max-w-full border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-4 mb-4 text-center">
          <div className="text-muted-foreground mb-2">🖼️ Image unavailable</div>
          {alt && (
            <div className="text-sm text-muted-foreground mb-2">{alt}</div>
          )}
        </div>
      );
    }

    const srcString = typeof src === "string" ? src : URL.createObjectURL(src);

    return (
      <img
        src={srcString}
        alt={alt}
        title={title}
        className="max-w-full h-auto rounded-lg shadow-md mb-4 cursor-pointer"
        loading="lazy"
        onClick={() => window.open(srcString, "_blank")}
        {...props}
      />
    );
  },

  // Strong and emphasis
  strong: ({ children, ref, node, ...props }) => (
    <strong className="font-semibold" {...props}>
      {children}
    </strong>
  ),
  em: ({ children, ref, node, ...props }) => (
    <em className="italic" {...props}>
      {children}
    </em>
  ),

  // Horizontal rule
  hr: () => null,

  // Task list items (checkboxes)
  input: ({ ref, node, type, checked, children, ...props }) => {
    if (type === "checkbox") {
      return (
        <input
          type="checkbox"
          checked={checked}
          disabled
          className="mr-2"
          {...props}
        />
      );
    }
    return <input type={type} {...props} />;
  },
};
